import { Analytics } from './analytics';
import { gql } from '@apollo/client';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  ItemsContext_VariantsPackFieldsFragment,
  MinimalScanningVariantPackFieldsFragment,
} from '@/types';
import { OnDeviceScanningSearchType } from '@/contexts/dynamic-config';
import { PACK_FRAGMENT } from '@/contexts/items-context-queries';
import { PackUnit } from '@/enums';
import { ScanningEventProperties } from '@/components/scanner/scanner-types';

export const MINIMAL_SCANNING_VARIANT_PACK_FRAGMENT = gql`
  fragment MinimalScanningVariantPackFields on variants_packs {
    external_item_id
    item {
      display_name
      restaurant_item_overrides(
        where: { restaurant_uuid: { _eq: $restaurantUUID } }
      ) {
        display_name
        uuid
      }
      uuid
    }
    pack {
      ...ItemsContext_PackFields
    }
    upc_list
    uuid
  }

  ${PACK_FRAGMENT}
`;

export const adaptMinimalScanningVariantPackToStandardFragment = (
  minimalScanningVariantPack: MinimalScanningVariantPackFieldsFragment,
): ItemsContext_VariantsPackFieldsFragment => {
  return { ...minimalScanningVariantPack, modifier_options: [] };
};

interface OnDeviceMatchResult {
  codeMatch: boolean;
  nameMatch: boolean;
}

export const isNotTagVariantPack = (
  item: ItemsContext_VariantsPackFieldsFragment,
) => {
  return item.pack.unit !== PackUnit.Tag;
};

export const exactMatchFn = (code: string, matchingFields: string[]) => {
  const lowerCode = code.toLowerCase();
  return matchingFields.some((field) => field.toLowerCase() === lowerCode);
};

export const partialMatchFn = (code: string, matchingFields: string[]) => {
  const lowerCode = code.toLowerCase();

  return matchingFields.some((field) => {
    const lowerField = field.toLowerCase();
    return lowerField.includes(lowerCode);
  });
};

export const getMatchedCode = (
  entry: { external_item_id: string | null; upc_list: string[] | null },
  query: string,
): string | null => {
  const lowerQuery = query.toLowerCase();
  const codes = [entry.external_item_id, ...(entry.upc_list ?? [])].filter(
    (c): c is string => Boolean(c),
  );
  return (
    codes.find((c) => c.toLowerCase() === lowerQuery) ??
    codes.find((c) => c.toLowerCase().includes(lowerQuery)) ??
    null
  );
};

const NUMERIC_REGEX = /^\d+$/;
const ZEROS_REGEX = /^0+$/;

// For _exactly 6-digit_ search queries (lower rank is better)
// Exact external_id > exact UPC > partial external_id > partial UPC > rest
const getSixDigitCodeRank = (code: string, query: string, isUPC: boolean) => {
  if (code === query) {
    return isUPC ? 1 : 0;
  }
  if (code.includes(query)) {
    return isUPC ? 3 : 2;
  }
  return Infinity;
};

// For _5 or less digit_ queries (numeric only, lower rank is better)
// Table for scenarios resulting in exact tier number (with adjustment for UPC)
// ┌──────────────────┬─────────────┬─────┐
// │       tier       │ external ID │ UPC │
// ├──────────────────┼─────────────┼─────┤
// │ 0 exact          │ 0           │ 1   │
// ├──────────────────┼─────────────┼─────┤
// | 1 leading zeros  │ 2           │ 3   │
// ├──────────────────┼─────────────┼─────┤
// │ 2 trailing zeros │ 4           │ 5   │
// ├──────────────────┼─────────────┼─────┤
// │ 3 6-digit        │ 6           │ 7   │
// ├──────────────────┼─────────────┼─────┤
// │ 4 rest           │ 8           │ 9   │
// └──────────────────┴─────────────┴─────┘
const getShortCodeRank = (code: string, query: string, isUPC: boolean) => {
  let tier: number;
  if (code === query) {
    tier = 0;
  } else if (
    code.endsWith(query) &&
    ZEROS_REGEX.test(code.slice(0, -query.length))
  ) {
    tier = 1;
  } else if (
    code.startsWith(query) &&
    ZEROS_REGEX.test(code.slice(query.length))
  ) {
    tier = 2;
  } else if (!code.includes(query)) {
    return Infinity;
  } else if (code.length === 6 && NUMERIC_REGEX.test(code)) {
    tier = 3;
  } else {
    tier = 4;
  }
  return tier * 2 + (isUPC ? 1 : 0); // multiple by 2 so the optional isUPC addition can fit in
};

// Handles matching specific cases which need special result sorting (each has different rules):
// 1. Exactly 6-digit numeric search
// 2. 5 or less digits numeric search
export const sortByCodeMatchPriority = <
  T extends { external_item_id: string | null; upc_list: string[] | null },
>(
  entries: T[],
  query: string,
): T[] => {
  // >6 characters or non-numeric means it doesn't belong to the special cases
  const lowerQuery = query.toLowerCase();
  if (!NUMERIC_REGEX.test(lowerQuery) || lowerQuery.length > 6) {
    return entries;
  }

  const getCodeRank =
    lowerQuery.length === 6 ? getSixDigitCodeRank : getShortCodeRank;
  const getEntryRank = (entry: T) => {
    let rank = entry.external_item_id
      ? getCodeRank(entry.external_item_id.toLowerCase(), lowerQuery, false)
      : Infinity;
    for (const upc of entry.upc_list ?? []) {
      if (upc) {
        rank = Math.min(rank, getCodeRank(upc.toLowerCase(), lowerQuery, true));
      }
    }
    return rank;
  };

  const ranks = new Map(entries.map((entry) => [entry, getEntryRank(entry)]));
  return [...entries].sort((a, b) => ranks.get(a)! - ranks.get(b)!);
};

export const isMatchingFn = (
  code: string,
  matchType: OnDeviceScanningSearchType,
  option: ItemListOption,
): OnDeviceMatchResult => {
  const { displayName, externalID, upcList } = option.metadata!;
  const codeFields = [externalID ?? '', ...(upcList ?? [])].filter(Boolean);

  const matchingFn =
    matchType === OnDeviceScanningSearchType.EXACT_MATCH
      ? exactMatchFn
      : partialMatchFn;

  return {
    codeMatch: matchingFn(code, codeFields),
    // Always use substring matching for display name
    nameMatch: displayName.toLowerCase().includes(code.toLowerCase()),
  };
};

/**
 * Builds a unique list of variant packs, excluding those with a unit type Tag
 * or that are already in the existingUUIDs set.
 *
 * @param {ItemsContext_VariantsPackFieldsFragment[]} variantPacks - The list of variant packs to filter.
 * @param {Set<string>} existingUUIDs - A set of UUIDs to exclude from the result.
 * @returns {ItemsContext_VariantsPackFieldsFragment[]} - The filtered list of unique variant packs.
 */
export const buildUniqueVariantPackList = (
  variantPacks: ItemsContext_VariantsPackFieldsFragment[],
  existingUUIDs: Set<string>,
): ItemsContext_VariantsPackFieldsFragment[] => {
  return variantPacks.filter(
    (vp) => isNotTagVariantPack(vp) && !existingUUIDs.has(vp.uuid),
  );
};

export const trackScanningEvent = (properties: ScanningEventProperties) => {
  Analytics.track('scan_event', properties);
};
